import { useState, useEffect } from "react";
import { GoogleLogin } from "@react-oauth/google";
import { IconPhone, IconChat, IconMapPin, IconCheckCircle } from "./Icons";
import { getBusinessReviews, createReview, deleteMyReview } from "../services/reviewService";
import { googleSignIn } from "../services/userService";

// ── Star helpers ──────────────────────────────────────────────────────────────
function StarRating({ value, onChange, size = 28 }) {
  const [hovered, setHovered] = useState(0);
  return (
    <div className="review-stars-input" aria-label="Select rating">
      {[1, 2, 3, 4, 5].map((n) => (
        <button
          key={n}
          type="button"
          className={`star-btn ${n <= (hovered || value) ? "filled" : ""}`}
          style={{ fontSize: size }}
          onMouseEnter={() => setHovered(n)}
          onMouseLeave={() => setHovered(0)}
          onClick={() => onChange(n)}
          aria-label={`${n} star`}
        >★</button>
      ))}
    </div>
  );
}

function StarDisplay({ value, total }) {
  const filled = Math.round(Number(value));
  return (
    <span className="review-stars-display" aria-label={`${value} out of 5`}>
      {[1, 2, 3, 4, 5].map((n) => (
        <span key={n} className={n <= filled ? "star-filled" : "star-empty"}>★</span>
      ))}
      <span className="review-avg-label">{value} ({total} review{total !== 1 ? "s" : ""})</span>
    </span>
  );
}

// ── Main component ────────────────────────────────────────────────────────────
function BusinessDetails({ business, onClose }) {
  const [imgIndex, setImgIndex] = useState(0);

  // Reviews
  const [reviews,     setReviews]     = useState([]);
  const [avgRating,   setAvgRating]   = useState(null);
  const [loadingRevs, setLoadingRevs] = useState(false);

  // Login / review flow
  // step: null | 'signin' | 'form'
  const [step,       setStep]       = useState(null);
  const [signingIn,  setSigningIn]  = useState(false);
  const [loginError, setLoginError] = useState("");

  // Review form
  const [rating,     setRating]     = useState(0);
  const [comment,    setComment]    = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [reviewMsg,  setReviewMsg]  = useState("");

  const userToken  = localStorage.getItem("userToken");
  const userName   = localStorage.getItem("userName") || "";
  const userPic    = localStorage.getItem("userPicture") || "";
  const isLoggedIn = Boolean(userToken);

  // Reset state whenever a different business is opened
  useEffect(() => {
    setImgIndex(0);
    setReviews([]);
    setAvgRating(null);
    setStep(null);
    setLoginError("");
    setRating(0);
    setComment("");
    setReviewMsg("");
    if (business?._id) fetchReviews(business._id);
  }, [business?._id]); // eslint-disable-line react-hooks/exhaustive-deps

  // Close on Escape
  useEffect(() => {
    const handler = (e) => { if (e.key === "Escape") onClose(); };
    document.addEventListener("keydown", handler);
    return () => document.removeEventListener("keydown", handler);
  }, [onClose]);

  // SEO per business
  useEffect(() => {
    if (!business) return;
    const origTitle = document.title;
    const metaEl    = document.querySelector('meta[name="description"]');
    const origMeta  = metaEl?.getAttribute("content") || "";

    document.title = `${business.businessName} - ${business.category} in ${business.city} | BusinessNearMe`;
    if (metaEl) {
      const raw = business.services
        ? `${business.businessName}: ${business.services}. Located in ${business.city}.`
        : `${business.businessName} — ${business.category} in ${business.city}.`;
      metaEl.setAttribute("content", raw.slice(0, 160));
    }

    document.getElementById("business-ld-json")?.remove();
    const script = document.createElement("script");
    script.type = "application/ld+json";
    script.id   = "business-ld-json";
    const ld = {
      "@context": "https://schema.org",
      "@type": "LocalBusiness",
      name: business.businessName,
      address: { "@type": "PostalAddress", addressLocality: business.city, addressCountry: "IN" }
    };
    if (business.services) ld.description = business.services;
    if (business.phone)    ld.telephone   = business.phone;
    if (business.email)    ld.email       = business.email;
    if (business.address)  ld.address.streetAddress = business.address;
    if (business.website)  ld.url = business.website.startsWith("http") ? business.website : `https://${business.website}`;
    if (business.images?.length > 0) {
      const first = business.images[0];
      const url = typeof first === "string" ? first : first?.url;
      if (url) ld.image = url;
    }
    if (business.location?.coordinates?.length === 2) {
      ld.geo = { "@type": "GeoCoordinates", latitude: business.location.coordinates[1], longitude: business.location.coordinates[0] };
    }
    script.textContent = JSON.stringify(ld);
    document.head.appendChild(script);

    return () => {
      document.title = origTitle;
      if (metaEl) metaEl.setAttribute("content", origMeta);
      document.getElementById("business-ld-json")?.remove();
    };
  }, [business]);

  if (!business) return null;

  // Image helpers
  const resolveImage = (item) => {
    if (!item) return "";
    if (typeof item === "string") return item;
    return item.url || item.image || item.imageUrl || "";
  };
  const rawImages = business.images?.length > 0 ? business.images : ["/no-image.svg"];
  const images    = rawImages.map(resolveImage).filter(Boolean);
  if (images.length === 0) images.push("/no-image.svg");

  const cleanPhone = business.phone?.replace(/[^0-9]/g, "");
  const mapLink    = business.location?.coordinates?.length === 2
    ? `https://www.google.com/maps?q=${business.location.coordinates[1]},${business.location.coordinates[0]}`
    : null;

  // Reviews
  const fetchReviews = async (id) => {
    setLoadingRevs(true);
    try {
      const data = await getBusinessReviews(id);
      setReviews(data.reviews || []);
      setAvgRating(data.avgRating);
    } catch { /* non-critical */ }
    finally { setLoadingRevs(false); }
  };

  // Google sign-in success
  const handleGoogleSuccess = async (credential) => {
    setLoginError("");
    setSigningIn(true);
    try {
      const data = await googleSignIn(credential);
      localStorage.setItem("userToken",   data.token);
      localStorage.setItem("userName",    data.name    || "");
      localStorage.setItem("userPicture", data.picture || "");
      setStep("form");
    } catch (err) {
      setLoginError(err?.response?.data?.msg || "Google sign-in failed. Please try again.");
    } finally {
      setSigningIn(false);
    }
  };

  // Write review button
  const handleWriteReview = () => {
    setReviewMsg("");
    setStep(isLoggedIn ? "form" : "signin");
  };

  // Submit review
  const handleSubmitReview = async () => {
    setReviewMsg("");
    if (rating === 0) { setReviewMsg("Please select a star rating."); return; }
    setSubmitting(true);
    try {
      await createReview(business._id, rating, comment);
      setReviewMsg("Thank you! Your review has been submitted.");
      setRating(0);
      setComment("");
      setStep(null);
      fetchReviews(business._id);
    } catch (err) {
      setReviewMsg(err?.response?.data?.msg || "Failed to submit review.");
    } finally {
      setSubmitting(false);
    }
  };

  const handleDeleteReview = async (reviewId) => {
    if (!window.confirm("Delete your review?")) return;
    try {
      await deleteMyReview(reviewId);
      fetchReviews(business._id);
    } catch { /* ignore */ }
  };

  const handleSignOut = () => {
    localStorage.removeItem("userToken");
    localStorage.removeItem("userName");
    localStorage.removeItem("userPicture");
    setStep(null);
  };

  // ── Render ───────────────────────────────────────────────────────────────────
  return (
    <div className="modal-overlay" onClick={onClose} role="dialog" aria-modal="true"
      aria-label={`${business.businessName} details`}>
      <div className="modal-card" onClick={(e) => e.stopPropagation()}>

        <button className="close-btn" onClick={onClose} aria-label="Close">✕</button>

        <div className="modal-scroll-area">

          {/* IMAGE SLIDER */}
          <div className="modal-image">
            <img src={images[imgIndex]}
              alt={`${business.businessName} - ${business.category}`}
              loading="lazy" decoding="async" />
            {images.length > 1 && (
              <>
                <button className="modal-slider-btn left" aria-label="Previous image"
                  onClick={(e) => { e.stopPropagation(); setImgIndex((p) => (p - 1 + images.length) % images.length); }}>‹</button>
                <button className="modal-slider-btn right" aria-label="Next image"
                  onClick={(e) => { e.stopPropagation(); setImgIndex((p) => (p + 1) % images.length); }}>›</button>
                <div className="modal-dots" aria-hidden="true">
                  {images.map((_, i) => (
                    <span key={i} className={`modal-dot ${i === imgIndex ? "active" : ""}`} />
                  ))}
                </div>
              </>
            )}
          </div>

          {/* BUSINESS INFO */}
          <div className="modal-content">
            <h2>{business.businessName}</h2>
            <p className="category">{business.category}</p>

            {business.verified && (
              <span className="verified-badge-modal">
                <IconCheckCircle size={13} /> Verified Business
              </span>
            )}

            {avgRating && reviews.length > 0 && (
              <div className="review-summary">
                <StarDisplay value={avgRating} total={reviews.length} />
              </div>
            )}

            <div className="biz-detail-grid">
              <div className="biz-detail-row">
                <span className="biz-detail-key">City</span>
                <span className="biz-detail-val">{business.city}</span>
              </div>
              <div className="biz-detail-row">
                <span className="biz-detail-key">Owner</span>
                <span className="biz-detail-val">{business.ownerName}</span>
              </div>
              {cleanPhone && (
                <div className="biz-detail-row">
                  <span className="biz-detail-key">Phone</span>
                  <span className="biz-detail-val">
                    {cleanPhone}
                    {business.mobileVerified && (
                      <span className="mobile-verified-badge-modal"> ✓ Verified</span>
                    )}
                  </span>
                </div>
              )}
              {business.address && (
                <div className="biz-detail-row">
                  <span className="biz-detail-key">Address</span>
                  <span className="biz-detail-val break-word">{business.address}</span>
                </div>
              )}
              {business.email && (
                <div className="biz-detail-row">
                  <span className="biz-detail-key">Email</span>
                  <span className="biz-detail-val">{business.email}</span>
                </div>
              )}
              {business.website && (
                <div className="biz-detail-row">
                  <span className="biz-detail-key">Website</span>
                  <span className="biz-detail-val">
                    <a href={business.website.startsWith("http") ? business.website : `https://${business.website}`}
                      target="_blank" rel="noreferrer" className="website-link">
                      {business.website}
                    </a>
                  </span>
                </div>
              )}
            </div>

            {business.services && (
              <>
                <p className="modal-info-label">Services</p>
                <p className="modal-info-value">{business.services}</p>
              </>
            )}
            {business.description && (
              <>
                <p className="modal-info-label">About</p>
                <p className="modal-info-value break-word">{business.description}</p>
              </>
            )}

            {/* ── REVIEWS SECTION ─────────────────────────────────────────── */}
            <div className="review-section">

              <div className="review-section-header">
                <p className="modal-info-label" style={{ margin: 0 }}>
                  Reviews {reviews.length > 0 ? `(${reviews.length})` : ""}
                </p>

                <div className="review-header-right">
                  {/* Signed-in user badge */}
                  {isLoggedIn && (
                    <div className="user-badge">
                      {userPic && (
                        <img src={userPic} alt={userName} className="user-avatar" referrerPolicy="no-referrer" />
                      )}
                      <span className="user-badge-name">{userName}</span>
                      <button className="user-signout-btn" onClick={handleSignOut} title="Sign out">✕</button>
                    </div>
                  )}

                  {step === null && (
                    <button className="review-write-btn" onClick={handleWriteReview}>
                      Write a Review
                    </button>
                  )}
                </div>
              </div>

              {/* ── Google Sign-In step ── */}
              {step === "signin" && (
                <div className="review-login-box">
                  <p className="review-login-title">Sign in to write a review</p>
                  <p className="review-login-subtitle">
                    Your Google account keeps your review linked to you.
                  </p>
                  {loginError && <div className="review-login-error">{loginError}</div>}

                  <div className="google-btn-wrap">
                    {signingIn ? (
                      <p className="review-loading">Signing in...</p>
                    ) : (
                      <GoogleLogin
                        onSuccess={(res) => handleGoogleSuccess(res.credential)}
                        onError={() => setLoginError("Google sign-in failed. Please try again.")}
                        useOneTap={false}
                        text="signin_with"
                        shape="rectangular"
                        theme="outline"
                        size="large"
                        width="260"
                      />
                    )}
                  </div>

                  <div style={{ textAlign: "center", marginTop: 10 }}>
                    <button className="review-btn-ghost" onClick={() => { setStep(null); setLoginError(""); }}>
                      Cancel
                    </button>
                  </div>
                </div>
              )}

              {/* ── Review form step ── */}
              {step === "form" && (
                <div className="review-form-box">
                  <p className="review-login-title">
                    {userName ? `Hi ${userName}! Rate this business:` : "Rate this business:"}
                  </p>
                  <StarRating value={rating} onChange={setRating} />
                  <textarea
                    className="review-textarea"
                    placeholder="Share your experience (optional · max 500 chars)"
                    value={comment}
                    onChange={(e) => setComment(e.target.value)}
                    maxLength={500}
                    rows={3}
                  />
                  {reviewMsg && (
                    <div className={reviewMsg.startsWith("Thank") ? "review-success" : "review-login-error"}>
                      {reviewMsg}
                    </div>
                  )}
                  <div className="review-login-actions">
                    <button className="review-btn-primary" onClick={handleSubmitReview} disabled={submitting}>
                      {submitting ? "Submitting..." : "Submit Review"}
                    </button>
                    <button className="review-btn-ghost" onClick={() => { setStep(null); setReviewMsg(""); }}>
                      Cancel
                    </button>
                  </div>
                </div>
              )}

              {/* Success message */}
              {step === null && reviewMsg.startsWith("Thank") && (
                <div className="review-success">{reviewMsg}</div>
              )}

              {/* Review list */}
              {loadingRevs ? (
                <p className="review-loading">Loading reviews...</p>
              ) : reviews.length === 0 ? (
                <p className="review-empty">No reviews yet. Be the first!</p>
              ) : (
                <div className="review-list">
                  {reviews.map((r) => (
                    <div key={r._id} className="review-item">
                      <div className="review-item-header">
                        <span className="review-stars-display">
                          {[1, 2, 3, 4, 5].map((n) => (
                            <span key={n} className={n <= r.rating ? "star-filled" : "star-empty"}>★</span>
                          ))}
                        </span>
                        <span className="review-author">{r.userName}</span>
                        <span className="review-date">
                          {new Date(r.createdAt).toLocaleDateString("en-IN", {
                            day: "numeric", month: "short", year: "numeric"
                          })}
                        </span>
                        {isLoggedIn && (
                          <button className="review-delete-btn"
                            onClick={() => handleDeleteReview(r._id)} title="Delete my review">✕</button>
                        )}
                      </div>
                      {r.comment && <p className="review-comment">{r.comment}</p>}
                    </div>
                  ))}
                </div>
              )}

            </div>
            {/* ── end REVIEWS ── */}

          </div>
        </div>{/* end modal-scroll-area */}

        {/* ACTIONS — pinned at bottom */}
        {(cleanPhone || mapLink) && (
          <div className="modal-actions">
            {cleanPhone && (
              <a href={`tel:${cleanPhone}`} className="btn call">
                <IconPhone size={15} /> Call
              </a>
            )}
            {cleanPhone && (
              <a href={`https://wa.me/${cleanPhone}`} target="_blank" rel="noreferrer" className="btn whatsapp">
                <IconChat size={15} /> WhatsApp
              </a>
            )}
            {mapLink && (
              <a href={mapLink} target="_blank" rel="noreferrer" className="btn map">
                <IconMapPin size={15} /> Map
              </a>
            )}
          </div>
        )}

      </div>
    </div>
  );
}

export default BusinessDetails;
